/**
 * SpineFlow — the guided first-run Spine sequence (docs/OLMPS_SPINE_SPEC.md §6).
 * Chains TELOS (per sector) → MoyRi (per gear) → KYRS (SHAPES list) → TKNE
 * (projects), resuming wherever `nextSpineStep(loadSpine(...))` says to.
 * Teaching order of the starting five: ORACLE → NUMA → KYRS → METIS → CLIO; TELOS
 * (Endeavours) and MoyRi (malady diagnostic + Goal) run here as TYTN tools, not
 * scored starters. Entered from onboarding or re-entered from ORACLE. Modeled on SessionFlow.tsx.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { GlassCard } from '../v10/ui';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import { GEARS, GEAR_META, SECTORS, SECTOR_LABELS, type Gear, type Sector } from '../domain/olmps';
import { Atmosphere, GlassPanel, GlassPill, PrimaryButton } from './glass';
import { SpineTelos } from './SpineTelos';
import { GearIntakeFlow } from './GearIntakeFlow';
import { SHAPE_CASCADE } from './shapeCascade';
import { loadSpine, saveKyrsDone } from './spine-data';
import {
  EMPTY_SPINE,
  nextSpineStep,
  type GearIntakeResult,
  type SpineState,
  type SpineStep,
} from './spine-types';

type FlowStep = 'intro' | SpineStep;

// Every dot in the bottom whole-setup meter, in order. 5 TELOS sectors →
// 5 MoyRi gears → KYRS (11 total). `activeIndex` is derived from spine state.
const SETUP_DOTS: { tag: string }[] = [
  ...SECTORS.map((s) => ({ tag: `ATLAS · ${SECTOR_LABELS[s]}` })),
  ...GEARS.map((g) => ({ tag: `MoyRi · ${GEAR_META[g].name}` })),
  { tag: 'KYRS · SHAPES' },
];

export function SpineFlow({
  userId,
  onExit,
  onOpenTool,
}: {
  userId: string | undefined;
  onExit: () => void;
  onOpenTool: (toolId: string) => void;
}) {
  const styles = useStyles();
  const { c } = useTheme();
  const [loading, setLoading] = useState(true);
  const [spine, setSpine] = useState<SpineState>(EMPTY_SPINE);
  const [step, setStep] = useState<FlowStep>('intro');

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const s = await loadSpine(supabase, userId);
      if (!cancelled) {
        setSpine(s);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const pendingSectors = useMemo(() => SECTORS.filter((sec) => !spine.telos[sec]?.done), [spine]);
  const pendingGears = useMemo(() => GEARS.filter((g) => !spine.moyri[g]), [spine]);

  const afterTelosSave = async () => {
    if (!userId) return;
    const s = await loadSpine(supabase, userId);
    setSpine(s);
    setStep(nextSpineStep(s));
  };

  const afterMoyriSave = (result: GearIntakeResult) => {
    const next: SpineState = { ...spine, moyri: { ...spine.moyri, [result.gear]: result } };
    setSpine(next);
    setStep(nextSpineStep(next));
  };

  const afterKyrsSave = (listId: string | null) => {
    const next: SpineState = { ...spine, kyrs: { shapesListId: listId, done: true } };
    setSpine(next);
    setStep(nextSpineStep(next));
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Atmosphere />
        <View style={styles.centerLoading}>
          <ActivityIndicator color={c.blueText} />
        </View>
      </View>
    );
  }

  const completedSectors = 5 - pendingSectors.length;
  const completedGears = 5 - pendingGears.length;

  // Where we are in the whole-setup dot meter (0-based over SETUP_DOTS).
  let dotIndex = -1;
  if (step === 'telos') dotIndex = completedSectors;
  else if (step === 'moyri') dotIndex = 5 + completedGears;
  else if (step === 'kyrs') dotIndex = 10;

  return (
    <View style={styles.container}>
      <Atmosphere />
      <Pressable style={styles.exitButton} onPress={onExit} hitSlop={10}>
        <Text style={styles.exitButtonText}>✕</Text>
      </Pressable>

      <View style={styles.content}>
        {step === 'intro' && <IntroStep onBegin={() => setStep(nextSpineStep(spine))} />}

        {step === 'telos' && pendingSectors[0] && (
          <View style={styles.stepColumn}>
            <StepMeter
              steps={SECTORS.map((s) => ({ key: s, label: SECTOR_LABELS[s] }))}
              activeIndex={completedSectors}
              top
            />
            <View style={styles.stepBody}>
              {/* key by sector so each sector remounts with fresh state —
                  without it, the previous sector's aims/step/answers leak in
                  (stale aims silently fill the 3-aim cap, back lands mid-flow). */}
              <SpineTelos key={pendingSectors[0]} userId={userId} sector={pendingSectors[0]} onDone={afterTelosSave} />
            </View>
          </View>
        )}

        {step === 'moyri' && pendingGears[0] && (
          <View style={styles.stepColumn}>
            <StepMeter
              steps={GEARS.map((g) => ({ key: g, label: GEAR_META[g].name }))}
              activeIndex={completedGears}
              top
            />
            <View style={styles.stepBody}>
              {/* key by gear — same reason as SpineTelos above. */}
              <GearIntakeFlow key={pendingGears[0]} userId={userId} gear={pendingGears[0]} onDone={afterMoyriSave} />
            </View>
          </View>
        )}

        {step === 'kyrs' && (
          <ScrollView contentContainerStyle={styles.scroll}>
            <Text style={styles.kicker}>KYRS · SHAPES</Text>
            <KyrsStep userId={userId} spine={spine} onDone={afterKyrsSave} onOpenTool={onOpenTool} />
          </ScrollView>
        )}

        {step === 'done' && (
          <ScrollView contentContainerStyle={styles.scroll}>
            <DoneStep onExit={onExit} />
          </ScrollView>
        )}
      </View>

      {dotIndex >= 0 && <SetupDots activeIndex={dotIndex} tag={SETUP_DOTS[dotIndex].tag} />}
    </View>
  );
}

// ── Step meter: connected circles with labels (per-sector / per-gear) ───────

function StepMeter({
  steps,
  activeIndex,
  top,
}: {
  steps: { key: string; label: string }[];
  activeIndex: number;
  top?: boolean;
}) {
  const styles = useStyles();
  return (
    <View style={[styles.stepMeter, top && styles.stepMeterTop]}>
      <View style={styles.stepMeterRow}>
        {steps.map((s, i) => {
          const done = i < activeIndex;
          const active = i === activeIndex;
          return (
            <View key={s.key} style={styles.stepCol}>
              <View style={styles.stepTrack}>
                <View
                  style={[
                    styles.stepLine,
                    i === 0 && styles.stepLineHidden,
                    i <= activeIndex && styles.stepLineDone,
                  ]}
                />
                <GlassCard style={[styles.stepCircle, done && styles.stepCircleDone, active && styles.stepCircleActive]}>
                  <Text style={[styles.stepNum, (done || active) && styles.stepNumOn]}>
                    {done ? '✓' : i + 1}
                  </Text>
                </GlassCard>
                <View
                  style={[
                    styles.stepLine,
                    i === steps.length - 1 && styles.stepLineHidden,
                    i < activeIndex && styles.stepLineDone,
                  ]}
                />
              </View>
              <Text
                style={[styles.stepLabel, active && styles.stepLabelActive]}
                numberOfLines={1}
              >
                {s.label}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

// ── Setup dots: whole-process meter pinned at the bottom ────────────────────

function SetupDots({ activeIndex, tag }: { activeIndex: number; tag: string }) {
  const styles = useStyles();
  return (
    <View style={styles.setupDots}>
      <View style={styles.dotRow}>
        {SETUP_DOTS.map((_, i) => {
          const done = i < activeIndex;
          const active = i === activeIndex;
          return <View key={i} style={[styles.dot, done && styles.dotDone, active && styles.dotActive]} />;
        })}
      </View>
      <Text style={styles.dotTag}>{tag}</Text>
    </View>
  );
}

// ── Intro step ───────────────────────────────────────────────────────────

function IntroStep({ onBegin }: { onBegin: () => void }) {
  const styles = useStyles();
  return (
    <ScrollView contentContainerStyle={styles.scroll}>
      <Text style={styles.kicker}>THE SPINE</Text>
      <GlassPanel strong style={styles.panel}>
        <Text style={styles.title}>Order the mind.</Text>
        <View style={styles.introLines}>
          <Text style={styles.introLine}>
            <Text style={styles.introLineBold}>Set the direction</Text> — ATLAS.
          </Text>
          <Text style={styles.introLine}>
            <Text style={styles.introLineBold}>Read the operator</Text> — MoyRi.
          </Text>
          <Text style={styles.introLine}>
            <Text style={styles.introLineBold}>Load the machine</Text> — KYRS.
          </Text>
          <Text style={styles.introLine}>
            ATLAS and MoyRi are TYTN tools you use here. Your starting five, taught in order: ORACLE · NUMA · KYRS · METIS · CLIO.
          </Text>
        </View>
        <PrimaryButton label="Begin" onPress={onBegin} />
      </GlassPanel>
    </ScrollView>
  );
}

// ── KYRS step: build & confirm the SHAPES list ──────────────────────────────

interface ShapesCandidate {
  id: string;
  sector: Sector;
  text: string;
}

function buildCandidates(spine: SpineState): ShapesCandidate[] {
  const out: ShapesCandidate[] = [];
  for (const sector of SECTORS) {
    const setup = spine.telos[sector];
    if (!setup?.done) continue;
    const cascade = SHAPE_CASCADE[sector];
    const deity = cascade.deities.find((d) => d.id === setup.deityId);
    const level = deity?.levels.find((l) => l.level === setup.deityLevel);
    (level?.activities ?? []).forEach((text, i) => {
      out.push({ id: `${sector}-act-${i}`, sector, text });
    });
    setup.aims.forEach((aim, ai) => {
      aim.protocol.forEach((text, pi) => {
        out.push({ id: `${sector}-aim${ai}-p${pi}`, sector, text });
      });
    });
  }
  return out;
}

function KyrsStep({
  userId,
  spine,
  onDone,
  onOpenTool,
}: {
  userId: string | undefined;
  spine: SpineState;
  onDone: (listId: string | null) => void;
  onOpenTool: (toolId: string) => void;
}) {
  const styles = useStyles();
  const { c } = useTheme();
  const alreadyCreated = !!spine.kyrs.shapesListId;
  const [listId, setListId] = useState<string | null>(spine.kyrs.shapesListId);
  const [creating, setCreating] = useState(!alreadyCreated);
  const [saving, setSaving] = useState(false);
  const candidates = useMemo(() => buildCandidates(spine), [spine]);
  const [kept, setKept] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(candidates.map((c) => [c.id, true]))
  );

  useEffect(() => {
    if (alreadyCreated || !userId) {
      setCreating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('task_lists')
        .insert({
          user_id: userId,
          name: 'SHAPES',
          include_in_all_tasks_view: true,
          categories: SECTORS.map((s) => SECTOR_LABELS[s]),
        })
        .select('id')
        .single();
      if (cancelled) return;
      if (!error && data?.id) setListId(data.id);
      setCreating(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const [sectorIndex, setSectorIndex] = useState(0);

  const toggle = (id: string) => setKept((k) => ({ ...k, [id]: !k[id] }));

  const confirm = async () => {
    if (!userId || !listId) return;
    setSaving(true);
    const rows = candidates
      .filter((c) => kept[c.id])
      .map((c) => ({
        user_id: userId,
        list_id: listId,
        title: c.text,
        category: SECTOR_LABELS[c.sector],
        sector: c.sector,
        done: false,
      }));
    if (rows.length > 0) {
      await supabase.from('tasks').insert(rows);
    }
    await saveKyrsDone(supabase, userId, listId);
    setSaving(false);
    onDone(listId);
  };

  const confirmExisting = async () => {
    if (!userId) return;
    setSaving(true);
    await saveKyrsDone(supabase, userId, listId);
    setSaving(false);
    onDone(listId);
  };

  if (alreadyCreated) {
    return (
      <GlassPanel strong style={styles.panel}>
        <Text style={styles.title}>Your SHAPES list is already set up.</Text>
        <Text style={styles.body}>Continue to load the rest of the spine, or open KYRS to add more.</Text>
        <View style={styles.stack}>
          <PrimaryButton label="Continue" onPress={confirmExisting} disabled={saving} />
          <GlassPill label="Open KYRS to import/add your own tasks" onPress={() => onOpenTool('kyrs')} />
        </View>
      </GlassPanel>
    );
  }

  if (creating) {
    return (
      <View style={styles.centerLoadingInline}>
        <ActivityIndicator color={c.blueText} />
      </View>
    );
  }

  const grouped = SECTORS.map((sector) => ({
    sector,
    items: candidates.filter((c) => c.sector === sector),
  })).filter((g) => g.items.length > 0);

  if (grouped.length === 0) {
    return (
      <GlassPanel strong style={styles.panel}>
        <Text style={styles.title}>Load your SHAPES actions.</Text>
        <Text style={styles.body}>No candidates found — open KYRS to add tasks manually.</Text>
        <View style={styles.stack}>
          <PrimaryButton label="Confirm" onPress={confirm} disabled={saving} />
          <GlassPill label="Open KYRS to import/add your own tasks" onPress={() => onOpenTool('kyrs')} />
        </View>
      </GlassPanel>
    );
  }

  const safeIndex = Math.min(sectorIndex, grouped.length - 1);
  const current = grouped[safeIndex];
  const isLast = safeIndex >= grouped.length - 1;

  return (
    <GlassPanel strong style={styles.panel}>
      <Text style={styles.title}>Load your SHAPES actions.</Text>
      <Text style={styles.body}>
        These come from the deity activities and aim protocols you set in ATLAS. Keep what you&apos;ll
        actually do — remove the rest. One area at a time.
      </Text>
      <View style={styles.candidateGroup}>
        <StepMeter
          steps={grouped.map((g) => ({ key: g.sector, label: SECTOR_LABELS[g.sector] }))}
          activeIndex={safeIndex}
        />
        <View style={styles.stack}>
          {current.items.map((c) => (
            <Pressable
              key={c.id}
              onPress={() => toggle(c.id)}
              style={[styles.option, kept[c.id] && styles.optionActive]}
            >
              <Text style={[styles.optionText, kept[c.id] && styles.optionTextActive]}>
                {kept[c.id] ? '☑' : '☐'} {c.text}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
      <View style={styles.stack}>
        {isLast ? (
          <PrimaryButton label="Confirm" onPress={confirm} disabled={saving} />
        ) : (
          <PrimaryButton label="Next area" onPress={() => setSectorIndex(safeIndex + 1)} disabled={saving} />
        )}
        {safeIndex > 0 && (
          <GlassPill label="Back" onPress={() => setSectorIndex(safeIndex - 1)} />
        )}
        <GlassPill label="Open KYRS to import/add your own tasks" onPress={() => onOpenTool('kyrs')} />
      </View>
    </GlassPanel>
  );
}

// ── Done step ────────────────────────────────────────────────────────────

function DoneStep({ onExit }: { onExit: () => void }) {
  const styles = useStyles();
  return (
    <GlassPanel strong style={styles.panel}>
      <Text style={styles.title}>The spine is set.</Text>
      <Text style={styles.body}>ORDER is running. Today is loaded — the rest is living it.</Text>
      <PrimaryButton label="Go to ORACLE" onPress={onExit} />
    </GlassPanel>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  container: { flex: 1 },
  content: { flex: 1 },
  centerLoading: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  centerLoadingInline: { padding: 40, alignItems: 'center', justifyContent: 'center' },

  exitButton: {
    position: 'absolute',
    top: 6,
    right: 12,
    zIndex: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  exitButtonText: { fontSize: 15, color: c.slate, fontWeight: '700' },

  stepColumn: { flex: 1 },
  stepBody: { flex: 1 },
  // Per-sector / per-gear step meter (connected circles + labels)
  stepMeter: { paddingHorizontal: 12, paddingBottom: 8 },
  stepMeterTop: { paddingTop: 44 },
  stepMeterRow: { flexDirection: 'row', alignItems: 'flex-start' },
  stepCol: { flex: 1, alignItems: 'center' },
  stepTrack: { flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch', justifyContent: 'center' },
  stepLine: { flex: 1, height: 2, backgroundColor: t.lineFaint },
  stepLineHidden: { backgroundColor: 'transparent' },
  stepLineDone: { backgroundColor: c.blue },
  stepCircle: {
    padding: 0,
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  stepCircleDone: { backgroundColor: c.primary, ...neu.glowPrimary },
  stepCircleActive: { backgroundColor: c.primary, ...neu.glowPrimary },
  stepNum: { fontSize: 12, fontWeight: '800', color: c.textFaint },
  stepNumOn: { color: t.onGold },
  stepLabel: { fontFamily: DISPLAY_FONT, marginTop: 6, fontSize: 9, fontWeight: '700', letterSpacing: 0.5, textTransform: 'uppercase', color: c.textFaint, textAlign: 'center' },
  stepLabelActive: { color: c.blueText },

  // Whole-setup dot meter, pinned at the bottom
  setupDots: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 20, alignItems: 'center', gap: 8 },
  dotRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7 },
  dot: { width: 7, height: 7, borderRadius: 4, backgroundColor: t.dotOff },
  dotDone: { backgroundColor: c.blue },
  dotActive: { width: 11, height: 11, borderRadius: 6, backgroundColor: c.primary, ...neu.glowPrimary },
  dotTag: { fontFamily: DISPLAY_FONT, fontSize: 10, fontWeight: '800', letterSpacing: 1.5, textTransform: 'uppercase', color: c.purple },

  scroll: { padding: 20, paddingTop: 50, gap: 16, paddingBottom: 60 },
  kicker: { fontFamily: DISPLAY_FONT, fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: c.textFaint, textAlign: 'center' },
  panel: { gap: 14 },
  title: { fontSize: 19, fontWeight: '700', color: c.ink, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  body: { fontSize: 14, color: c.textMuted, lineHeight: 20 },
  stack: { gap: 10 },

  introLines: { gap: 8 },
  introLine: { fontSize: 15, color: c.textMuted, lineHeight: 22 },
  introLineBold: { fontWeight: '800', color: c.ink },

  candidateGroup: { gap: 6 },

  option: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    paddingHorizontal: 16,
    paddingVertical: 12,
    ...neu.raisedSm,
  },
  optionActive: { backgroundColor: c.lime, ...neu.inset },
  optionText: { fontSize: 14, color: c.slate },
  optionTextActive: { color: c.blueText },
}),
);
